import React from 'react';
import { Button } from '../components/ui/button';
import { Check, Phone, Zap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const Endolifting = () => {
  const lang = useLanguage();
  const t = getPageTexts('endolifting', lang);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#d4af37] to-[#8b7355]"></div>
        
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <a href="tel:952764547" className="text-[#8b7355] font-semibold hover:underline">
              952 76 45 47
            </a>
          </div>
          
          <div className="flex items-center justify-center gap-3 mb-6">
            <Zap className="text-[#d4af37]" size={40} />
            <span className="bg-[#d4af37] text-white px-4 py-1 rounded-none text-sm font-medium">
              {t.badge}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1C1C1A] text-center mb-6 leading-tight">
            {t.title}
          </h1>
          
          <p className="text-lg md:text-xl text-text-secondary text-center mb-4 max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: t.heroDesc1 }} />
          
          <p className="text-lg text-text-secondary text-center mb-8" dangerouslySetInnerHTML={{ __html: t.heroDesc2 }} />
          
          <div className="text-center">
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#d4af37] hover:bg-[#b8960c] text-white px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {t.requestConsult}
            </Button>
          </div>
        </div>
      </section>

      {/* Placeholder for Before/After Images */}
      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] mb-8">
              {t.resultsTitle}
            </h2>
            <div className="bg-ivory rounded-none shadow-lg p-12 border-2 border-dashed border-gray-300">
              <p className="text-text-muted text-lg">
                {t.comingSoon}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is Endolifting Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">
            {t.whatIsTitle}
          </h2>
          
          <p className="text-lg text-text-secondary mb-6" dangerouslySetInnerHTML={{ __html: t.whatIsDesc1 }} />
          
          <p className="text-lg text-text-secondary mb-8" dangerouslySetInnerHTML={{ __html: t.whatIsDesc2 }} />

          <h3 className="text-2xl font-light text-[#1C1C1A] mb-6">
            {t.isForYou}
          </h3>
          
          <ul className="space-y-4 mb-12">
            <li className="flex items-start gap-3">
              <Check className="text-[#d4af37] mt-1 flex-shrink-0" size={20} />
              <span className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t.sign1 }} />
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-[#d4af37] mt-1 flex-shrink-0" size={20} />
              <span className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t.sign2 }} />
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-[#d4af37] mt-1 flex-shrink-0" size={20} />
              <span className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t.sign3 }} />
            </li>
            <li className="flex items-start gap-3">
              <Check className="text-[#d4af37] mt-1 flex-shrink-0" size={20} />
              <span className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t.sign4 }} />
            </li>
          </ul>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-[#1C1C1A] to-[#1a2a3f] px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <Zap className="text-[#d4af37] mx-auto mb-4" size={48} />
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
              {t.advantagesTitle}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{t.adv1Title}</h3>
              <p className="text-white/80">{t.adv1Desc}</p>
            </div>
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{t.adv2Title}</h3>
              <p className="text-white/80">{t.adv2Desc}</p>
            </div>
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{t.adv3Title}</h3>
              <p className="text-white/80">{t.adv3Desc}</p>
            </div>
            <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
              <Check className="text-[#d4af37] mb-3" size={24} />
              <h3 className="text-white font-semibold mb-2">{t.adv4Title}</h3>
              <p className="text-white/80">{t.adv4Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8 text-center">
            {t.zonesTitle}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            {t.zones && t.zones.map((zone, index) => (
              <div key={index} className="bg-cream rounded-none p-6">
                <p className="text-[#1C1C1A] font-medium">{zone}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="py-20 bg-cream px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/3">
              <img 
                src="/images/foto-angel-bata.webp"
                alt="Dr. Ángel Norro"
                className="w-full h-auto rounded-none shadow-lg"
              />
            </div>
            
            <div className="lg:w-2/3">
              <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">
                {t.doctorTitle}
              </h2>
              
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: t.doctorDesc1 }} />
              
              <p className="text-text-secondary mb-4" dangerouslySetInnerHTML={{ __html: t.doctorDesc2 }} />
              
              <p className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t.doctorDesc3 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-[#1C1C1A] px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <Zap className="text-[#d4af37] mx-auto mb-4" size={48} />
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            {t.ctaTitle}
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            {t.ctaDesc}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:952764547"
              className="inline-flex items-center justify-center gap-2 bg-ivory text-[#1C1C1A] px-8 py-4 rounded-none font-semibold hover:bg-gray-100 transition-colors"
            >
              <Phone size={20} />
              952 76 45 47
            </a>
            <a 
              href="https://wa.me/34696345792"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-gold500 text-white px-8 py-4 rounded-none font-semibold hover:bg-gold600 transition-colors"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Endolifting;
